import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  DollarSign, Clock, Wrench, TrendingUp, AlertCircle, MessageSquare, 
  Bell, Users, Reply, Send, X 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    potentialQuotesCount: 0,
    potentialQuotesValue: 0,
    totalCurrentRevenue: 0,
    outstandingRevenue: 0,
    owed15Days: 0,
    owed30Days: 0,
    totalLabourHours: 0,
    plannedLabourHours: 0,
    actualLabourHours: 0,
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [messageStats, setMessageStats] = useState({ total_count: 0, high_priority_count: 0 });
  const [loading, setLoading] = useState(true);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyType, setReplyType] = useState('resolution');
  const [replyPriority, setReplyPriority] = useState('normal');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [dashboardRes, projectsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/messages/pending-actions`).catch(() => ({ data: { all_messages: [], total_count: 0, high_priority_count: 0 } }))
      ]);

      const dashboardData = dashboardRes.data;
      const projects = projectsRes.data;
      const messagesData = messagesRes.data;

      setStats({
        potentialQuotesCount: dashboardData.potential_quotes_30_days.count,
        potentialQuotesValue: dashboardData.potential_quotes_30_days.total_value,
        totalCurrentRevenue: dashboardData.total_current_revenue,
        outstandingRevenue: dashboardData.outstanding_revenue,
        owed15Days: dashboardData.aged_receivables.owed_15_days,
        owed30Days: dashboardData.aged_receivables.owed_30_days,
        totalLabourHours: dashboardData.labour_hours.total,
        plannedLabourHours: dashboardData.labour_hours.planned_from_quotes,
        actualLabourHours: dashboardData.labour_hours.actual_from_projects,
      });

      setRecentProjects(projects.slice(0, 5));
      setPendingMessages(messagesData.all_messages || []);
      setMessageStats({
        total_count: messagesData.total_count || 0,
        high_priority_count: messagesData.high_priority_count || 0
      });
    } catch (error) {
      console.error('Failed to load dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReply = (message, e) => {
    e.stopPropagation(); // Prevent card click navigation
    setSelectedMessage(message);
    setReplyText('');
    setReplyType('resolution');
    setReplyPriority(message.priority === 'urgent' || message.priority === 'high' ? 'high' : 'normal');
    setReplyModalOpen(true);
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(`${API}/projects/${selectedMessage.project_id}/messages`, {
        project_id: selectedMessage.project_id,
        message: replyText,
        message_type: replyType,
        priority: replyPriority,
        replied_to: selectedMessage.id,
        sender_id: 'temp',
        sender_name: 'temp',
        sender_email: 'temp',
        sender_role: 'temp'
      });

      toast.success('Reply sent successfully!');
      setReplyModalOpen(false);
      setSelectedMessage(null);
      setReplyText('');
      
      // Reload dashboard data to update counts
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to send reply');
      console.error(error);
    } finally {
      setSendingReply(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, onClick, subtitle }) => (
    <div 
      className="border border-border rounded-lg hover:border-primary/50 transition-all cursor-pointer hover:shadow-lg bg-card" 
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={onClick}
    >
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">{title}</p>
            <p className={`text-3xl font-bold font-mono ${color}`}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1 font-mono">{subtitle}</p>}
          </div>
          <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
            <Icon className="text-primary" size={24} />
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2" data-testid="dashboard-title">
          CONTROL CENTER
        </h1>
        <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
          Shop operations overview
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <StatCard
          title="Potential Quote Projects"
          subtitle="Last 30 days (unconverted)"
          value={`${stats.potentialQuotesCount}`}
          icon={TrendingUp}
          color="text-yellow-500"
          onClick={() => navigate('/quote-manager')}
        />
        <StatCard
          title="Total Current Revenue"
          subtitle={`Value: $${stats.totalCurrentRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          value={`$${stats.totalCurrentRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={DollarSign}
          color="text-green-500"
          onClick={() => navigate('/invoices')}
        />
        <StatCard
          title="Outstanding Revenue"
          subtitle="Unpaid invoices"
          value={`$${stats.outstandingRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          icon={AlertCircle}
          color="text-orange-500"
          onClick={() => navigate('/invoices')}
        />
      </div>
      
      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="border border-border rounded-lg hover:border-primary/30 transition-colors cursor-pointer bg-card" onClick={() => navigate('/quote-manager')}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Potential Quotes (30 Days)</h3>
              <TrendingUp className="text-yellow-500" size={20} />
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Unconverted Quotes:</span>
                <span className="text-lg font-bold font-mono text-yellow-500">{stats.potentialQuotesCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Potential Value:</span>
                <span className="text-lg font-bold font-mono text-yellow-500">${stats.potentialQuotesValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-xs text-muted-foreground italic">Click to view quotes and follow up with customers</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="border border-border rounded-lg hover:border-primary/30 transition-colors cursor-pointer bg-card" onClick={() => navigate('/invoices')}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Revenue Breakdown</h3>
              <DollarSign className="text-green-500" size={20} />
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total Invoiced:</span>
                <span className="text-lg font-bold font-mono text-green-500">${stats.totalCurrentRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Outstanding:</span>
                <span className="text-lg font-bold font-mono text-orange-500">${stats.outstandingRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">15+ Days Past Due:</span>
                  <span className="text-sm font-bold font-mono text-red-500">${stats.owed15Days.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">30+ Days Past Due:</span>
                  <span className="text-sm font-bold font-mono text-red-600">${stats.owed30Days.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-xs text-muted-foreground italic">Click to view invoices and follow up on payments</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Labour Hours Card */}
      <div className="mb-8">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-mono uppercase tracking-widest text-muted-foreground">Labour Hours Breakdown</h3>
              <Wrench className="text-primary" size={20} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Planned (from Quotes)</p>
                <p className="text-3xl font-bold font-mono text-blue-500">{stats.plannedLabourHours.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-2">Actual (from Projects)</p>
                <p className="text-3xl font-bold font-mono text-primary">{stats.actualLabourHours.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h</p>
              </div>
              <div className="text-center border-l border-border pl-6">
                <p className="text-sm text-muted-foreground mb-2">Total Labour Hours</p>
                <p className="text-3xl font-bold font-mono text-green-500">{stats.totalLabourHours.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Updates & Immediate Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Immediate Actions - High Priority */}
        <Card className={`border-border ${messageStats.high_priority_count > 0 ? 'border-red-500/50 shadow-lg shadow-red-500/10' : ''}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className={messageStats.high_priority_count > 0 ? "text-red-500 animate-pulse" : "text-primary"} />
                <CardTitle className="text-xl font-bold uppercase tracking-tight">
                  Immediate Actions
                </CardTitle>
              </div>
              {messageStats.high_priority_count > 0 && (
                <Badge variant="destructive" className="text-xs font-mono animate-pulse">
                  {messageStats.high_priority_count} URGENT
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {pendingMessages.filter(m => m.priority === 'high' || m.priority === 'urgent').length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Bell className="mx-auto mb-3 opacity-20" size={48} />
                <p className="text-sm">No urgent customer messages</p>
                <p className="text-xs mt-1">All caught up! 🎉</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {pendingMessages.filter(m => m.priority === 'high' || m.priority === 'urgent').slice(0, 5).map((msg) => (
                  <div
                    key={msg.id}
                    className="p-4 bg-red-50/50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-sm hover:border-red-400 transition-colors cursor-pointer"
                    onClick={() => navigate(`/projects/${msg.project_id}`)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertCircle size={16} className="text-red-500" />
                          <p className="font-semibold text-sm">{msg.project_name}</p>
                        </div>
                        <p className="text-xs text-muted-foreground font-mono">
                          From: {msg.sender_name} • {new Date(msg.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant="destructive" className="text-xs uppercase">
                        {msg.priority}
                      </Badge>
                    </div>
                    <p className="text-sm line-clamp-2 text-muted-foreground">
                      {msg.message}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {msg.message_type}
                        </Badge>
                        <span className="text-xs text-muted-foreground font-mono">
                          Status: {msg.status}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => handleOpenReply(msg, e)}
                        className="text-xs font-mono uppercase tracking-wider"
                      >
                        <Reply size={14} className="mr-1" />
                        Reply
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Customer Updates - All Pending */}
        <Card className="border-border">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="text-primary" />
                <CardTitle className="text-xl font-bold uppercase tracking-tight">
                  Customer Updates
                </CardTitle>
              </div>
              {messageStats.total_count > 0 && (
                <Badge variant="secondary" className="text-xs font-mono">
                  {messageStats.total_count} pending
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {pendingMessages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="mx-auto mb-3 opacity-20" size={48} />
                <p className="text-sm">No pending customer messages</p>
                <p className="text-xs mt-1">All customer inquiries resolved</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {pendingMessages.slice(0, 5).map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-4 border rounded-sm hover:border-primary transition-colors cursor-pointer ${
                      msg.priority === 'high' || msg.priority === 'urgent'
                        ? 'bg-blue-50/50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'
                        : 'bg-card border-border'
                    }`}
                    onClick={() => navigate(`/projects/${msg.project_id}`)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{msg.project_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          From: {msg.sender_name} • {new Date(msg.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {(msg.priority === 'high' || msg.priority === 'urgent') && (
                        <Badge variant="destructive" className="text-xs">
                          {msg.priority}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm line-clamp-2 text-muted-foreground">
                      {msg.message}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <Badge variant="outline" className="text-xs">
                        {msg.message_type}
                      </Badge>
                      <Button
                        size="sm"
                        variant={msg.priority === 'high' || msg.priority === 'urgent' ? 'destructive' : 'default'}
                        onClick={(e) => handleOpenReply(msg, e)}
                        className="text-xs font-mono uppercase tracking-wider"
                      >
                        <Reply size={14} className="mr-1" />
                        Reply
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Projects */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
            Recent Projects
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentProjects.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No projects yet. Create your first quote!</p>
          ) : (
            <div className="space-y-3">
              {recentProjects.map((project) => (
                <div
                  key={project.id}
                  data-testid={`recent-project-${project.id}`}
                  className="flex items-center justify-between p-4 bg-card border border-border rounded-sm hover:border-primary transition-colors"
                >
                  <div>
                    <h3 className="font-medium text-base">{project.project_name}</h3>
                    <p className="text-sm text-muted-foreground">{project.client_name}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-mono text-sm font-medium">
                        ${project.quote_total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {project.actual_labour_hours}h labour
                      </p>
                    </div>
                    <span
                      className={`
                        status-badge
                        ${
                          project.status === 'Completed'
                            ? 'bg-green-500/10 text-green-500 border-green-500/20'
                            : project.status === 'In Progress'
                            ? 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                            : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                        }
                      `}
                    >
                      {project.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reply Modal */}
      {replyModalOpen && selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            {/* Modal Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold uppercase tracking-tight mb-1">
                    Reply to Customer
                  </h2>
                  <p className="text-sm text-muted-foreground font-mono">
                    {selectedMessage.project_name}
                  </p>
                </div>
                <button
                  onClick={() => setReplyModalOpen(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Original Message */}
            <div className="p-6 bg-muted/30 border-b border-border">
              <div className="flex items-center gap-2 mb-3">
                <Badge variant={selectedMessage.priority === 'urgent' || selectedMessage.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs uppercase">
                  {selectedMessage.priority}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {selectedMessage.message_type}
                </Badge>
                <span className="text-xs text-muted-foreground font-mono">
                  From: {selectedMessage.sender_name}
                </span>
              </div>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                {selectedMessage.message}
              </p>
            </div>

            {/* Reply Form */}
            <div className="p-6 space-y-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wide mb-2 block">
                  Your Response
                </Label>
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your response here..."
                  className="min-h-[150px] font-mono text-sm"
                  disabled={sendingReply}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide mb-2 block">
                    Response Type
                  </Label>
                  <select
                    value={replyType}
                    onChange={(e) => setReplyType(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-sm font-mono text-sm"
                    disabled={sendingReply}
                  >
                    <option value="comment">Comment</option>
                    <option value="resolution">Resolution</option>
                    <option value="update">Update</option>
                    <option value="question">Question</option>
                  </select>
                </div>

                <div>
                  <Label className="text-xs font-mono uppercase tracking-wide mb-2 block">
                    Priority
                  </Label>
                  <select
                    value={replyPriority}
                    onChange={(e) => setReplyPriority(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-input rounded-sm font-mono text-sm"
                    disabled={sendingReply}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-4">
                <Button
                  onClick={handleSendReply}
                  disabled={sendingReply || !replyText.trim()}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-mono uppercase tracking-wider"
                >
                  <Send size={16} className="mr-2" />
                  {sendingReply ? 'Sending...' : 'Send Reply'}
                </Button>
                <Button
                  onClick={() => setReplyModalOpen(false)}
                  variant="outline"
                  disabled={sendingReply}
                  className="font-mono uppercase tracking-wider"
                >
                  Cancel
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4">
                ✉️ Customer will receive an email notification after you send this reply
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
